import { Check, Copy } from "lucide-react";
import { useState } from "react";

import type { DocsCodeBlock as DocsCodeBlockData } from "@/presentation/data/docs/docs-content.types";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

interface DocsCodeBlockProps {
  block: DocsCodeBlockData;
}

export function DocsCodeBlock({ block }: DocsCodeBlockProps) {
  const [copied, setCopied] = useState(false);

  function copy() {
    const write = navigator.clipboard?.writeText(block.code);
    if (!write) return;
    write
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard unavailable / denied — fail gracefully, don't flip state.
      });
  }

  return (
    <figure className="my-4">
      <div className="overflow-hidden rounded-lg border border-[oklch(1_0_0_/_0.08)] bg-[oklch(0.16_0.025_240)]">
        <div className="flex items-center justify-between border-b border-[oklch(1_0_0_/_0.08)] px-3 py-1.5">
          <span className="font-mono text-[10px] tracking-[0.18em] text-[oklch(0.85_0.01_240_/_0.5)] uppercase">
            {block.language}
          </span>
          <button
            type="button"
            onClick={copy}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] text-[oklch(0.85_0.01_240_/_0.6)] transition-colors hover:text-[oklch(0.88_0.14_190)]"
          >
            {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <pre className="overflow-x-auto px-4 py-3">
          <code className="font-mono text-[12.5px] leading-relaxed text-[oklch(0.9_0.01_240)]">
            {block.code}
          </code>
        </pre>
      </div>
      {block.caption ? (
        <figcaption className={cn("text-muted-foreground mt-2 text-xs italic")}>
          {block.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
